import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { HardDrive, AlertTriangle, CheckCircle2, TrendingUp } from 'lucide-react'
import { getOrganizationStorageUsage, getOrganizationStorageLimit, formatBytes } from '../services/storageManagement'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from './Card'

const StorageUsageDisplay = () => {
  const organizationId = useOrganizationId()
  const [usage, setUsage] = useState(null)
  const [limits, setLimits] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (organizationId) {
      loadStorageInfo()
    }
  }, [organizationId])

  const loadStorageInfo = async () => {
    try {
      setLoading(true)
      const [usageResult, limitResult] = await Promise.all([
        getOrganizationStorageUsage(organizationId),
        getOrganizationStorageLimit(organizationId),
      ])

      if (usageResult.success) setUsage(usageResult.data)
      if (limitResult.success) setLimits(limitResult.data)
    } catch (error) {
      console.error('Error loading storage info:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card variant="soft" className="p-4">
        <div className="text-sm text-gray-500">Loading storage information...</div>
      </Card>
    )
  }

  if (!usage || !limits) {
    return null
  }

  const usagePercent = limits.maxStorageBytes > 0 
    ? (usage.total_bytes_used / limits.maxStorageBytes) * 100 
    : 0
  const remainingBytes = Math.max(0, limits.maxStorageBytes - usage.total_bytes_used)
  const isNearLimit = usagePercent >= 80
  const isOverLimit = usagePercent >= 100

  return (
    <Card variant="soft" className="p-4">
      <div className="flex items-center gap-3 mb-4">
        <HardDrive size={20} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Storage Usage</h3>
      </div>

      <div className="space-y-4">
        {/* Usage Bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {formatBytes(usage.total_bytes_used)} / {formatBytes(limits.maxStorageBytes)}
            </span>
            <span className={`text-sm font-semibold ${
              isOverLimit ? 'text-red-600' : isNearLimit ? 'text-yellow-600' : 'text-gray-600'
            }`}>
              {usagePercent.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(100, usagePercent)}%` }}
              transition={{ duration: 0.5 }}
              className={`h-full rounded-full ${
                isOverLimit
                  ? 'bg-red-500'
                  : isNearLimit
                  ? 'bg-yellow-500'
                  : 'bg-primary-500'
              }`}
            />
          </div>
        </div>

        {/* Status Message */}
        {isOverLimit ? (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertTriangle size={18} className="text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-700 dark:text-red-300">
              Storage limit reached. Please delete files or contact support to increase your limit.
            </p>
          </div>
        ) : isNearLimit ? (
          <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <AlertTriangle size={18} className="text-yellow-600 dark:text-yellow-400" />
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Storage is nearly full ({usagePercent.toFixed(1)}% used). Consider cleaning up old files.
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" />
            <p className="text-sm text-green-700 dark:text-green-300">
              {formatBytes(remainingBytes)} remaining
            </p>
          </div>
        )}

        {/* Additional Info */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Files</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {usage.total_files_count.toLocaleString()}
              {limits.maxFilesPerOrganization && (
                <span className="text-gray-500"> / {limits.maxFilesPerOrganization.toLocaleString()}</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Max File Size</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {limits.maxFileSizeBytes ? formatBytes(limits.maxFileSizeBytes) : 'N/A'}
            </p>
          </div>
        </div>

        {limits.isCustom && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <TrendingUp size={12} />
              Custom storage limit (overrides tier default)
            </p>
          </div>
        )}
      </div>
    </Card>
  )
}

export default StorageUsageDisplay

